import isEqual from 'lodash/isEqual'

import * as Sentry from '@sentry/react'

import AppStore from '~publish/legacy/composer/composer/stores/AppStore'
import ComposerStore from '~publish/legacy/composer/composer/stores/ComposerStore'

import type { ComposerStore as ZustandComposerStore } from '../composerStore'
import { projectFluxChannelContent } from './projectFluxChannelContent'

/** Fields compared per channel, in canonical order for stable reporting. */
export const CHANNEL_VARIANT_FIELDS = [
  'text',
  'urls',
  'unshortenedUrls',
  'assets',
  'aiAssisted',
  'updateType',
  'isReminder',
  'title',
  'enabledAttachmentType',
  'channelData',
  'sourceLink',
  'retweet',
  'selectedStickers',
  'threadItems',
] as const

// Coarse buckets for `sessionAgeMs`, so the Sentry issue's tag-distribution
// view shows how fresh a session was at drift time without opening every
// event — e.g. mostly-fresh sessions would point at a seed-time race, mostly
// long-lived ones at something that accumulates or never settles.
const bucketSessionAge = (ms: number): string => {
  if (ms < 1_000) return '<1s'
  if (ms < 10_000) return '<10s'
  if (ms < 60_000) return '<1min'
  if (ms < 600_000) return '<10min'
  return '10min+'
}

/**
 * Per-channel counterpart of `checkOmniParityAfterMirror`: compares each selected
 * channel's Zustand variant against its backing Flux service draft and reports
 * divergence. Reporting mechanics (dev `console.error` dragnet, 1%-sampled prod
 * Sentry event, `composer.bridge.drift` message) match that function.
 *
 * Channel-specific choices: the fingerprint carries a `channel` discriminator so
 * the tracks stay separate Sentry issues under the shared alertable message; the
 * high-cardinality channel id lives in `extra`, not `tags`; only ids, field
 * names, text lengths, and thread-item counts are logged — never content, and
 * never the metadata scalar, `channelData`, `sourceLink`, `retweet`, or
 * thread-item values (`updateType`/`title` are user-adjacent text,
 * `channelData` can carry user content like first-comment text, `retweet`
 * carries the quoted tweet's text and the user's comment, and thread items
 * carry post text). A channel with no variant yet is skipped (lazy seed, not
 * drift). A variant left behind for a channel that is no longer selected is
 * its own, separately-fingerprinted report (`kind=leftoverVariant`): there is
 * no Flux draft to project for a deselected channel, so it can't join the
 * per-field comparison above.
 *
 * `sessionAgeMs` — milliseconds since this mirror instance installed — and the
 * current `composerMode` (`create`/`edit`/`duplicate`, or `unknown` before
 * options load) are tagged so a captured event places itself in the
 * composer's lifecycle without breadcrumb archaeology.
 */
export function checkChannelParityAfterMirror(
  store: ZustandComposerStore,
  direction: 'fluxToZustand' | 'zustandToFlux',
  sessionAgeMs = 0,
): void {
  const isProduction = process.env.NODE_ENV === 'production'
  // 0.04 local sample x 0.25 Sentry client sampleRate (PublishSentryClient
  // applies it to every captured event) = ~1% of drift occurrences reported.
  // Keep in sync with the client rate.
  if (isProduction && Math.random() > 0.04) return

  const variants = store.getState().channelVariants
  const selectedProfileIds = new Set(
    AppStore.getSelectedProfiles().map((profile) => profile.id),
  )
  const leftoverVariantIds = Object.keys(variants).filter(
    (channelId) => !selectedProfileIds.has(channelId),
  )

  if (leftoverVariantIds.length > 0) {
    if (!isProduction) {
      // eslint-disable-next-line no-console
      console.error(
        '[composer.bridge] leftover channel variant for a deselected channel',
        `direction=${direction}`,
        leftoverVariantIds.join(','),
      )
    } else {
      Sentry.captureEvent({
        message: 'composer.bridge.drift',
        level: 'warning',
        fingerprint: [
          'composer-bridge-drift',
          'channel',
          direction,
          'leftoverVariant',
        ],
        tags: {
          direction,
          track: 'channel',
          kind: 'leftoverVariant',
          composerMode: AppStore.getOptions().composerMode ?? 'unknown',
          sessionAgeBucket: bucketSessionAge(sessionAgeMs),
        },
        extra: {
          leftoverVariantChannelCount: leftoverVariantIds.length,
          leftoverVariantChannels: leftoverVariantIds.join(','),
          sessionAgeMs,
        },
      })
    }
  }

  const drifts: Array<{
    channelId: string
    fields: string[]
    textLengthFlux: number
    textLengthZustand: number
    threadItemCountFlux: number
    threadItemCountZustand: number
  }> = []

  for (const profile of AppStore.getSelectedProfiles()) {
    const draft = ComposerStore.getDraft(profile.service.name)
    if (!draft) continue
    const variant = variants[profile.id]
    if (!variant) continue

    const flux = projectFluxChannelContent(draft)
    const driftedFields: string[] = []
    if (!isEqual(flux.text, variant.text)) driftedFields.push('text')
    if (!isEqual(flux.urls, variant.urls)) driftedFields.push('urls')
    if (!isEqual(flux.unshortenedUrls, variant.unshortenedUrls))
      driftedFields.push('unshortenedUrls')
    if (!isEqual(flux.assets, variant.assets)) driftedFields.push('assets')
    if (!isEqual(flux.aiAssisted, variant.aiAssisted))
      driftedFields.push('aiAssisted')
    if (!isEqual(flux.updateType, variant.updateType))
      driftedFields.push('updateType')
    if (!isEqual(flux.isReminder, variant.isReminder))
      driftedFields.push('isReminder')
    if (!isEqual(flux.title, variant.title)) driftedFields.push('title')
    if (!isEqual(flux.enabledAttachmentType, variant.enabledAttachmentType))
      driftedFields.push('enabledAttachmentType')
    if (!isEqual(flux.channelData, variant.channelData))
      driftedFields.push('channelData')
    if (!isEqual(flux.sourceLink, variant.sourceLink))
      driftedFields.push('sourceLink')
    if (!isEqual(flux.retweet, variant.retweet)) driftedFields.push('retweet')
    if (!isEqual(flux.selectedStickers, variant.selectedStickers))
      driftedFields.push('selectedStickers')
    if (!isEqual(flux.threadItems, variant.threadItems))
      driftedFields.push('threadItems')

    if (driftedFields.length > 0) {
      drifts.push({
        channelId: profile.id,
        fields: driftedFields,
        textLengthFlux: flux.text.length,
        textLengthZustand: variant.text.length,
        threadItemCountFlux: flux.threadItems.length,
        threadItemCountZustand: variant.threadItems.length,
      })
    }
  }

  if (drifts.length === 0) return

  const allFields = CHANNEL_VARIANT_FIELDS.filter((field) =>
    drifts.some((drift) => drift.fields.includes(field)),
  )

  // Flat string, shared by both reporting paths — not the raw `drifts` array.
  // `extra: { drifts }` (array of objects, each holding a `fields` array) put
  // the per-channel field breakdown three levels deep, past Sentry's default
  // `normalizeDepth: 3`; the SDK silently replaced it with the literal string
  // "[Array]", making a captured event's per-channel breakdown unrecoverable.
  // A flat string has no such depth to trip over, regardless of channel or
  // field count.
  const channelSummary = drifts
    .map(
      (drift) =>
        `${drift.channelId}:${drift.fields.join('|')}` +
        `(text ${drift.textLengthFlux}/${drift.textLengthZustand},` +
        `threads ${drift.threadItemCountFlux}/${drift.threadItemCountZustand})`,
    )
    .join(' ')

  if (!isProduction) {
    // eslint-disable-next-line no-console
    console.error(
      '[composer.bridge] channel mirror drift detected',
      `direction=${direction}`,
      `channels=${drifts.length}`,
      `fields=${allFields.join(',')}`,
      channelSummary,
    )
    return
  }

  const composerMode = AppStore.getOptions().composerMode ?? 'unknown'

  Sentry.captureEvent({
    message: 'composer.bridge.drift',
    level: 'warning',
    fingerprint: ['composer-bridge-drift', 'channel', direction],
    tags: {
      direction,
      track: 'channel',
      fields: allFields.join(','),
      composerMode,
      sessionAgeBucket: bucketSessionAge(sessionAgeMs),
    },
    extra: {
      driftedChannelCount: drifts.length,
      channels: channelSummary,
      sessionAgeMs,
    },
  })
  Sentry.addBreadcrumb({
    category: 'composer.bridge',
    level: 'warning',
    message: 'channel mirror drift',
    data: {
      direction,
      track: 'channel',
      channels: drifts.length,
      fields: allFields,
      composerMode,
      sessionAgeMs,
    },
  })
}
